import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { getMyStore, createOrderFull, getStorePaymentMethods } from '../../services/supabaseService';
import { CartItem } from '../../types';
import { StockProductPicker, StockProduct } from '../../components/shopkeeper/StockProductPicker';
import { CounterOrderSummary } from '../../components/shopkeeper/CounterOrderSummary';

interface PedidoBalcaoProps {
  cart: CartItem[];
  updateQuantity: (id: string, delta: number) => void;
  removeFromCart: (id: string) => void;
  clearCart: (storeId?: string | number) => void;
  addToCart?: (product: any) => void;
}

export const PedidoBalcao: React.FC<PedidoBalcaoProps> = ({
  cart,
  updateQuantity,
  removeFromCart,
  clearCart,
  addToCart,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [myStore, setMyStore] = useState<any>(null);
  const [loadingStore, setLoadingStore] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [storePaymentMethods, setStorePaymentMethods] = useState<any[]>([]);

  const isLojista = user?.role_id === 6 || user?.role_id === 1;

  // Load lojista's store
  useEffect(() => {
    let isMounted = true;
    const loadStore = async () => {
      if (!user || !isLojista) {
        setLoadingStore(false);
        return;
      }
      try {
        setLoadingStore(true);
        const store = await getMyStore();
        if (isMounted) {
          setMyStore(store);
          if (store?.id) {
            const methods = await getStorePaymentMethods(store.id);
            if (isMounted) setStorePaymentMethods(methods || []);
          }
        }
      } catch (err) {
        console.error('Erro ao buscar dados da loja:', err);
      } finally {
        if (isMounted) setLoadingStore(false);
      }
    };

    loadStore();
    return () => {
      isMounted = false;
    };
  }, [user?.id, isLojista]);

  // Filter items in cart that strictly belong to THIS store
  const balcaoCartItems = useMemo(() => {
    if (!myStore?.id) return [];
    return cart.filter(
      item => String(item.storeId || item.store_id) === String(myStore.id)
    );
  }, [cart, myStore?.id]);

  const total = useMemo(() => {
    return balcaoCartItems.reduce(
      (acc, item) => acc + (item.price || 0) * (item.quantity || 0),
      0
    );
  }, [balcaoCartItems]);

  const totalUnits = useMemo(() => {
    return balcaoCartItems.reduce((acc, item) => acc + (item.quantity || 0), 0);
  }, [balcaoCartItems]);

  // Handle adding an item from the in-stock list
  const handleAddStockItem = (stockItem: StockProduct) => {
    if (!stockItem.products || !myStore) return;

    const availableStock = Number(stockItem.quantity) || 0;
    const existing = balcaoCartItems.find(
      i => String(i.productId || i.id) === String(stockItem.products!.id)
    );

    if (existing && existing.quantity >= availableStock) {
      showNotification(`Estoque máximo disponível atingido (${availableStock} un)`, 'info');
      return;
    }

    if (addToCart) {
      addToCart({
        id: String(stockItem.products.id),
        slug: stockItem.products.slug || '',
        name: stockItem.products.name,
        price: Number(stockItem.store_price ?? stockItem.products.msrp) || 0,
        imageUrl: stockItem.products.image_url || undefined,
        image_url: stockItem.products.image_url || undefined,
        storeId: myStore.id,
        store_id: myStore.id,
        storeName: myStore.name,
        store_name: myStore.name,
        quantity: 1,
        stock: availableStock,
      });
      showNotification(`"${stockItem.products.name}" adicionado ao pedido.`, 'success');
    }
  };

  // Finalize Counter Sale
  const handleFinalizeSale = async () => {
    if (!myStore) {
      showNotification('Loja não encontrada', 'error');
      return;
    }

    if (balcaoCartItems.length === 0) {
      showNotification('Adicione produtos ao pedido para continuar.', 'info');
      return;
    }

    try {
      setIsProcessing(true);

      // Pedido do lojista no balcão é sempre em Dinheiro
      const cashMethod = storePaymentMethods.find(
        m => m.slug === 'cash' || m.slug === 'dinheiro' || m.name?.toLowerCase().includes('dinheiro')
      );
      const paymentMethodId = cashMethod?.id || 4; // Default Dinheiro

      const orderPayload = {
        store_id: myStore.id,
        shipping_method_id: 4, // Retirada no local / Venda no Balcão
        shipping_cost: 0,
        payment_method_id: paymentMethodId,
        payment_status: 'approved',
        status: 'completed',
        notes: customerNote ? `Venda de Balcão - Cliente: ${customerNote}` : 'Venda de Balcão (PDV Presencial)',
        items: balcaoCartItems.map(item => ({
          product_id: Number(item.productId || item.id),
          quantity: Number(item.quantity),
          price: Number(item.price)
        }))
      };

      const result = await createOrderFull(orderPayload);

      showNotification('Venda no balcão registrada com sucesso!', 'success');
      clearCart(myStore.id);
      navigate(`/pedido/${result.id}`);
    } catch (err: any) {
      console.error('Erro ao finalizar venda no balcão:', err);
      showNotification(err.message || 'Erro ao registrar venda no balcão', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isLojista) {
    return (
      <div className="max-w-xl mx-auto py-20 px-4 text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 text-2xl">
          <i className="fas fa-lock"></i>
        </div>
        <h1 className="text-xl font-black text-white">Acesso Restrito a Lojistas</h1>
        <p className="text-sm text-slate-400">
          A funcionalidade de <strong>Pedido no Balcão</strong> é exclusiva para lojas cadastradas emitirem vendas presenciais com seu estoque.
        </p>
        <button
          onClick={() => navigate('/carrinho')}
          className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-all shadow-lg"
        >
          Ir para o Meu Carrinho de Compras
        </button>
      </div>
    );
  }

  if (loadingStore) {
    return (
      <div className="py-24 flex flex-col items-center justify-center space-y-3">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">
          Carregando Pedido no Balcão...
        </p>
      </div>
    );
  }

  if (!myStore) {
    return (
      <div className="max-w-xl mx-auto py-20 px-4 text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 text-2xl">
          <i className="fas fa-store"></i>
        </div>
        <h1 className="text-xl font-black text-white">Nenhuma Loja Vinculada</h1>
        <p className="text-sm text-slate-400">
          Você possui perfil de lojista, mas nenhuma loja foi configurada para sua conta.
        </p>
        <button
          onClick={() => navigate('/minha-loja')}
          className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-all shadow-lg"
        >
          Configurar Minha Loja
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5 pb-16">
      {/* Header */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center space-x-2.5">
            <span className="p-2 rounded-lg bg-purple-950/60 border border-purple-800/60 text-purple-400">
              <i className="fas fa-cash-register text-base"></i>
            </span>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">Pedido no Balcão</h1>
              <p className="text-xs text-slate-400 font-medium">
                Ponto de Venda (PDV) da loja <strong className="text-purple-300">{myStore.name}</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {balcaoCartItems.length > 0 && (
            <button
              onClick={() => {
                clearCart(myStore.id);
                showNotification('Pedido do balcão esvaziado.', 'info');
              }}
              className="px-3 py-1.5 bg-red-950/40 hover:bg-red-900/50 text-red-400 border border-red-800/60 rounded-lg text-xs font-semibold flex items-center space-x-1.5"
            >
              <i className="fas fa-trash-can text-xs"></i>
              <span>Limpar Pedido</span>
            </button>
          )}
          <span className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs font-semibold text-slate-300">
            {totalUnits} {totalUnits === 1 ? 'unidade' : 'unidades'}
          </span>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left Column (8 cols): Active Items & In-Stock Catalog */}
        <div className="lg:col-span-8 space-y-5">
          {/* Current Order Items */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center space-x-2">
                <i className="fas fa-receipt text-purple-400 text-xs"></i>
                <h2 className="text-xs font-bold text-white uppercase tracking-wider">
                  Itens do Pedido Atual
                </h2>
              </div>
              <span className="text-xs text-slate-400">
                {balcaoCartItems.length} {balcaoCartItems.length === 1 ? 'produto adicionado' : 'produtos adicionados'}
              </span>
            </div>

            {balcaoCartItems.length === 0 ? (
              <div className="text-center py-8 px-4 bg-slate-950 rounded-lg border border-slate-800 space-y-1.5">
                <i className="fas fa-barcode text-slate-600 text-2xl"></i>
                <p className="text-sm font-semibold text-slate-300">Nenhum produto adicionado ao pedido.</p>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Selecione os produtos com estoque no painel abaixo para adicionar diretamente ao balcão.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                {balcaoCartItems.map(item => {
                  const maxStock = item.stock || 999;
                  const itemTotal = (item.price || 0) * (item.quantity || 0);

                  return (
                    <div
                      key={item.id}
                      className="p-2.5 bg-slate-950 rounded-lg border border-slate-800 flex items-center justify-between gap-3 hover:border-slate-700"
                    >
                      {/* Product details */}
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <div className="w-10 h-12 rounded-lg bg-slate-800 flex-shrink-0 overflow-hidden border border-slate-700/60 flex items-center justify-center">
                          {item.imageUrl || item.image_url ? (
                            <img
                              src={item.imageUrl || item.image_url}
                              alt={item.name}
                              className="w-full h-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <i className="fas fa-image text-slate-600 text-xs"></i>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-xs font-bold text-white truncate" title={item.name}>
                            {item.name}
                          </h4>
                          <div className="flex items-center space-x-2 text-[11px] text-slate-400 mt-0.5">
                            <span className="text-emerald-400 font-semibold">
                              R$ {(item.price || 0).toFixed(2).replace('.', ',')} un
                            </span>
                            {item.stock !== undefined && (
                              <span className="text-[10px] text-slate-500">
                                (máx {item.stock} un)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Quantity adjustment & Subtotal */}
                      <div className="flex items-center space-x-4 flex-shrink-0">
                        <div className="flex items-center space-x-1.5 bg-slate-900 border border-slate-700/80 rounded-lg p-1">
                          <button
                            onClick={() => updateQuantity(item.id, -1)}
                            className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition-all text-xs"
                            title="Diminuir"
                          >
                            <i className="fas fa-minus text-[9px]"></i>
                          </button>
                          <span className="w-6 text-center text-xs font-bold text-white">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => {
                              if (item.quantity >= maxStock) {
                                showNotification(`Limite do estoque atingido (${maxStock} un)`, 'info');
                                return;
                              }
                              updateQuantity(item.id, 1);
                            }}
                            disabled={item.quantity >= maxStock}
                            className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs"
                            title="Aumentar"
                          >
                            <i className="fas fa-plus text-[9px]"></i>
                          </button>
                        </div>

                        <div className="text-right w-20">
                          <span className="text-xs font-black text-white">
                            R$ {itemTotal.toFixed(2).replace('.', ',')}
                          </span>
                        </div>

                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="w-7 h-7 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-all"
                          title="Remover produto"
                        >
                          <i className="fas fa-times text-xs"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick-Add In-Stock Products */}
          <StockProductPicker
            myStoreId={myStore.id}
            onAddItem={handleAddStockItem}
            balcaoItems={balcaoCartItems}
          />
        </div>

        {/* Right Column (4 cols): Checkout & Payment Summary */}
        <div className="lg:col-span-4">
          <CounterOrderSummary
            total={total}
            itemCount={balcaoCartItems.length}
            cashReceived={cashReceived}
            setCashReceived={setCashReceived}
            customerNote={customerNote}
            setCustomerNote={setCustomerNote}
            isProcessing={isProcessing}
            onFinalizeSale={handleFinalizeSale}
          />
        </div>
      </div>
    </div>
  );
};
